// Tabla con búsqueda, orden y paginación (port de dataTable). Genérica sobre T.
import { useMemo, useState, type ReactNode } from 'react';

export interface Column<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
}

interface DataTableProps<T> {
  cols: Column<T>[];
  rows: T[];
  title?: string;
  search?: boolean;
  pageSize?: number;
  empty?: string;
  actions?: ReactNode;
}

function get<T>(row: T, key: string): unknown {
  return (row as Record<string, unknown>)[key];
}

export function DataTable<T>({ cols, rows, title, search = true, pageSize = 8, empty = 'Sin registros', actions }: DataTableProps<T>) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<string | null>(null);
  const [dir, setDir] = useState(1);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let r = rows;
    if (q) {
      const s = q.toLowerCase();
      r = r.filter((row) => cols.some((c) => String(get(row, c.key) ?? '').toLowerCase().includes(s)));
    }
    if (sort) {
      r = r.slice().sort((a, b) => {
        const va = get(a, sort) as string | number;
        const vb = get(b, sort) as string | number;
        const na = parseFloat(va as string);
        const nb = parseFloat(vb as string);
        const cmp = !isNaN(na) && !isNaN(nb) ? na - nb : String(va).localeCompare(String(vb));
        return cmp * dir;
      });
    }
    return r;
  }, [rows, q, sort, dir, cols]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageSafe = Math.min(page, pages);
  const slice = filtered.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

  const clickHeader = (c: Column<T>) => {
    if (c.sortable === false) return;
    if (sort === c.key) setDir((d) => d * -1);
    else { setSort(c.key); setDir(1); }
  };

  return (
    <div className="card table-card">
      <div className="table-toolbar">
        <h3>{title || ''}</h3>
        <div className="spacer" />
        {search ? (
          <div className="search" style={{ width: 220 }}>
            <i className="fa-solid fa-magnifying-glass" />
            <input placeholder="Buscar..." value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          </div>
        ) : null}
        {actions ? <div className="tbl-actions">{actions}</div> : null}
      </div>

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.key} onClick={() => clickHeader(c)}>
                  {c.label}
                  {c.sortable !== false ? (
                    <i className={`fa-solid ${sort === c.key ? (dir === 1 ? 'fa-arrow-up-short-wide' : 'fa-arrow-down-wide-short') : 'fa-sort'} sort-ico`} />
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slice.length ? (
              slice.map((row, i) => (
                <tr key={i}>
                  {cols.map((c) => <td key={c.key}>{c.render ? c.render(row) : String(get(row, c.key) ?? '')}</td>)}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={cols.length}>
                  <div className="empty-state"><i className="fa-solid fa-inbox" />{empty}</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="table-foot">
        <span>{filtered.length} registro(s)</span>
        <div className="pager">
          <button disabled={pageSafe <= 1} onClick={() => setPage(pageSafe - 1)} aria-label="Anterior"><i className="fa-solid fa-chevron-left" /></button>
          {Array.from({ length: pages }, (_, i) => (
            <button key={i} className={pageSafe === i + 1 ? 'active' : ''} onClick={() => setPage(i + 1)}>{i + 1}</button>
          ))}
          <button disabled={pageSafe >= pages} onClick={() => setPage(pageSafe + 1)} aria-label="Siguiente"><i className="fa-solid fa-chevron-right" /></button>
        </div>
      </div>
    </div>
  );
}
